import { wccModules } from 'enums/wccModules';
import { Container } from 'inversify';
import { Subscribable } from 'knockout';
import Messenger from 'libraries/messenger';
import { IWCCStorageManager } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import { StorageContext } from 'models/storage/context';
import router from 'plugins/router';
import { createExtendedContainer } from '../configuration/inversify/container/extended';
import { getSetting } from '../helpers/settings';

export default abstract class Shell {
    private scheduledRoute = ko.observable<string>();
    private activeRoutes = ko.observableArray<DurandalRouteConfiguration>();

    private isShellLoaded = ko.observable(false);
    private isFirstPageLoaded = ko.observable(false);

    private storageContext: StorageContext;
    private routes: Subscribable<Array<DurandalRouteConfiguration>>
    
    private isActiveRouteUpdateAllowed: Subscribable<boolean>

    protected container: Container
    protected storage: IWCCStorageManager
    protected effects: EffectsContainer

    css = '';
    breadcrumbs = ko.observableArray();

    ready: Subscribable<boolean>

    detached: Action

    cookieConsentActive = ko.pureComputed(() => getSetting('CookieConsentVersion2'));

    constructor() {
        const [container, disposeContainer] = createExtendedContainer();

        this.container = container;
        this.storage = this.container.get(wccModules.storage);
        this.effects = container.get(wccModules.effects);
        this.storageContext = this.effects.register(new StorageContext());

        this.detached = disposeContainer;

        this.routes = ko.pureComputed(() => this.getRoutes()).unwrap();
        
        const isRoutesUpdateRequired = ko.observable(true);
        const isUpdatingRoutes = ko.observable(false);
        const areRoutesReady = ko.observable(false);  

        const isRoutesUpdateAllowed = ko.pureComputed(() => {
            const routes = this.routes();
            const updateRequired = isRoutesUpdateRequired();
            const updateAllowed = this.checkIfUpdateAllowed();
            const updatingRoutes = isUpdatingRoutes();
            const shellLoaded = this.isShellLoaded();
            const firstPageLoaded = this.isFirstPageLoaded();
            const isNavigating = router.isNavigating();

            return updateRequired &&
                updateAllowed &&
                !updatingRoutes &&
                (!shellLoaded || firstPageLoaded) &&
                !isNavigating &&
                routes.length > 0;
        });

        this.isActiveRouteUpdateAllowed = ko.pureComputed(() => {
            const activeRoutes = this.activeRoutes();
            const updatingRoutes = isUpdatingRoutes();
            const updateRequired = isRoutesUpdateRequired();
            const updateAllowed = this.checkIfUpdateAllowed();
            const isNavigating = router.isNavigating();

            return updateAllowed &&
                activeRoutes.length > 0 &&
                !updatingRoutes &&
                !updateRequired &&
                !isNavigating;
        });

        this.ready = ko.pureComputed(() => areRoutesReady());

        requestAnimationFrame(() => {
            this.ready.when().then(() => router.activate());

            this.effects.register([
                this.routes.subscribe(() => isRoutesUpdateRequired(true)),
                this.activeRoutes.subscribe(routes => console.log('Active Routes', routes))
            ]);

            this.effects.register(updateAllowed => {
                if (updateAllowed) {
                    isUpdatingRoutes(true);
                    isRoutesUpdateRequired(false);

                    const routes = this.routes();

                    router.reset();
                    this.initRouter();

                    if (areRoutesReady() && this.scheduledRoute() == undefined)
                        this.scheduledRoute(location.hash.substr(1));

                    if (routes.length > 0) {
                        router.map(routes);
                        areRoutesReady(true);
                    }

                    this.activeRoutes(routes);
                    isUpdatingRoutes(false);
                }
            }, [isRoutesUpdateAllowed]);

            this.effects.register((route, updateAllowed) => {
                if (route && updateAllowed) {
                    this.scheduledRoute(undefined);
                    router.navigate(route);
                }
            }, [this.scheduledRoute, this.isActiveRouteUpdateAllowed]);            

            this.setInitialRoute();            
        });
    }

    getView() {
        return require('./shell.html');
    }

    activate() {
        this.beforeActivate();
        this.storageContext.create('shell').from(this);
        this.afterActivate();
    }

    protected checkIfUpdateAllowed(): boolean {
        return true;
    }

    protected abstract getRoutes(): Subscribable<Array<DurandalRouteConfiguration>>

    protected getInitialRoute(): string | undefined {
        return undefined;
    }

    protected beforeActivate() { }
    protected afterActivate() { }

    protected beforeCompositionComplete() { }
    protected afterCompositionComplete() { }

    private setInitialRoute() {
        const initialRoute = this.getInitialRoute();

        if (initialRoute)
            location.hash = initialRoute;
    }

    private initRouter() {
        router.on('router:route:not-found', this.onRouteNotFound.bind(this));
        router.on('router:navigation:processing', this.onRouteProcessing.bind(this));
        router.on('router:navigation:composition-complete', this.onCompositionComplete.bind(this));
        router.on('router:navigation:cancelled', this.onNavigationCancelled.bind(this));
    }

    private onRouteNotFound(url: string) {
        if (!this.isActiveRouteUpdateAllowed()) {
            this.scheduledRoute(url);
        } else if (this.activeRoutes().length > 0 && document.getElementById(url) == undefined) {
            router.navigate('');
        }
    }

    private onRouteProcessing(instruction: any, router: any) {
        console.log('activating route', instruction);

        if (!this.isActiveRouteUpdateAllowed() && router.activeInstruction() !== instruction)
            this.scheduledRoute(instruction.fragment);

        this.storageContext.readOnly(true);
        Messenger().hideAll();
    }

    private onCompositionComplete() {
        this.beforeCompositionComplete();

        if (!this.isShellLoaded())
            this.isShellLoaded(true);
        else if (!this.isFirstPageLoaded())
            this.isFirstPageLoaded(true);

        this.storageContext.readOnly(false);
        this.scrollToTop();

        this.afterCompositionComplete();
    }

    private onNavigationCancelled() {
        this.storageContext.readOnly(false)
    }

    private scrollToTop() {
        $('html, body').animate({ scrollTop: 0 });
    }
}